import { BaseModuleI, ModuleI, OptionI } from "../../interfaces";
import {
  getAllModulesByProfileId,
  parameterGetNoAuthByCode,
} from "../../apis";
import { create } from "zustand";
import { persist, subscribeWithSelector } from "zustand/middleware";
import { QUALITY_PHOTO_IMAGE_CODE } from "../../constants";
import { useModulesBackupStore } from "./router-backup.store";

type DashboardCodes =
  | "DASHBOARD_CLIENT"
  | "DASHBOARD_CLIENT_ADMINISTRATOR"
  | "DASHBOARD_ADMINISTRATOR"
  | null;

type State = {
  options: OptionI[];
  modules: BaseModuleI[];
  moduleMap: Record<string, BaseModuleI>;
  clientId: number | null;
  client: string | null;
  profileId: string;
  avatar: string;
  cloneCatalogue: number | null;
  cloneTask: number | null;
  section: string;
  typeDashboardCode: DashboardCodes;
  firstName: string;
  isLoading: boolean;
  previousModuleBase: string;
  fullName: string;
  photoWidth: { ideal: number };
  photoHeight: { ideal: number };
  // OPTIMIZACIÓN: Cache para evitar llamadas innecesarias
  lastModulesUpdate: number;
  isValidatingModules: boolean;
  processedContainersToday: Set<string>;
  currentDateKey: string;
  byPassSw:boolean;
};

type Actions = {
  setByPassSw: (byPassSw: State["byPassSw"]) => void;
  setClientId: (clientId: State["clientId"]) => void;
  setClient: (client: State["client"]) => void;
  setPhotoWidth: (photoWidth: State["photoWidth"]) => void;
  setPhotoHeight: (photoHeight: State["photoHeight"]) => void;
  setPreviousModuleBase: (
    previousModuleBase: State["previousModuleBase"]
  ) => void;
  setLoading: (loading: State["isLoading"]) => void;
  setProfileId: (profileId: string) => void;
  setFirstName: (firstName: string) => void;
  setTypeDashboardCode: (typeDashboardCode: DashboardCodes) => void;
  setOptions: (options: OptionI[]) => void;
  setModules: (modules: ModuleI[]) => void;
  setAvatar: (avatar: string) => void;
  setFullName: (fullName: string) => void;
  reset: () => void;
  resetOptions: () => void;
  validateModulesFromApi: () => Promise<any>;
  resetModules: () => void;
  getModulesFromApi: () => void;
  setCloneCatalogue: (catalogue: State["cloneCatalogue"]) => void;
  setCloneTask: (task: State["cloneTask"]) => void;
  setSection: (section: State["section"]) => void;
  getQualityPhoto: () => void;
  // OPTIMIZACIÓN: Nuevas acciones para mejor control
  forceRefreshModules: () => Promise<any>;
  getModulesFromCache: () => ModuleI[];
  addProcessedContainer: (container: string, type: 'empty' | 'full' | 'truck') => void;
  isContainerProcessedToday: (container: string, type: 'empty' | 'full') => boolean;
};

// OPTIMIZACIÓN: Función para comparar arrays de módulos
const areModulesEqual = (a: ModuleI[], b: ModuleI[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((mod, index) => mod.id === b[index]?.id && mod.to === b[index]?.to);
};

// OPTIMIZACIÓN: Función para comparar arrays de opciones
const areOptionsEqual = (a: OptionI[], b: OptionI[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((opt, index) => opt.id === b[index]?.id && opt.to === b[index]?.to);
};

const buildModuleMap = (
  modules: BaseModuleI[],
  map: Record<string, BaseModuleI> = {}
): Record<string, BaseModuleI> => {
  for (const module of modules) {
    if (module.to) {
      map[module.to] = module;
    }
    if (module.children && module.children.length > 0) {
      buildModuleMap(module.children, map);
    }
  }
  return map;
};

const initialState: State = {
  byPassSw: false,
  clientId: null,
  moduleMap: {},
  client: null,
  options: [],
  modules: [],
  profileId: "",
  firstName: "",
  avatar: "",
  typeDashboardCode: null,
  cloneCatalogue: null,
  cloneTask: null,
  section: "",
  isLoading: false,
  previousModuleBase: "",
  fullName: "",
  photoWidth: { ideal: 1920 },
  photoHeight: { ideal: 1200 },
  // OPTIMIZACIÓN: Nuevos campos
  lastModulesUpdate: 0,
  isValidatingModules: false,
  processedContainersToday: new Set(),
        currentDateKey: new Date().toDateString(),
};

export const useRouterStore = create<State & Actions>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        processedContainersToday: new Set(),
        currentDateKey: new Date().toDateString(),

        isContainerProcessedToday: (container: string, type: 'empty' | 'full') => {
          const today = new Date().toDateString();
          const state = get();
          
          // Auto-reset si cambió el día
          if (today !== state.currentDateKey) {
            set({ 
              processedContainersToday: new Set(),
              currentDateKey: today 
            });
            return false; // Nuevo día = no procesado
          }
          
          const key = `${container}-${type}`;
          return state.processedContainersToday.has(key);
        },

        // Marcar como procesado
        addProcessedContainer: (container: string, type: 'empty' | 'full' | 'truck') => {
          set((state) => {
            const newSet = new Set(state.processedContainersToday);
            const key = `${container}-${type}`;
            newSet.add(key);
            return { processedContainersToday: newSet };
          });
        },

        byPassSw: false,
        clientId: null,
        client: null,
        photoWidth: { ideal: 1920 },
        photoHeight: { ideal: 1200 },
        options: [],
        modules: [],
        moduleMap: {},
        profileId: "",
        firstName: "",
        avatar: "",
        cloneCatalogue: null,
        cloneTask: null,
        section: "",
        typeDashboardCode: null,
        previousModuleBase: "",
        isLoading: false,
        fullName: "",
        lastModulesUpdate: 0,
        isValidatingModules: false,
        setByPassSw: (byPassSw) => set(() => ({ byPassSw })),
        setClient: (client) => set(() => ({ client })),
        setClientId: (clientId) => set(() => ({ clientId })),
        setPhotoHeight: (photoHeight) => set(() => ({ photoHeight })),
        setPhotoWidth: (photoWidth) => set(() => ({ photoWidth })),
        setFullName: (fullName) => set((state) => ({ fullName })),
        setPreviousModuleBase: (previousModuleBase) =>
          set((state) => ({ previousModuleBase })),
        setLoading: (loading) => set({ isLoading: loading }),
        reset: () => set(initialState),
        setFirstName: (firstName) => set(() => ({ firstName })),
        setSection: (section) => set(() => ({ section })),
        
        // OPTIMIZACIÓN: Resetear opciones sin comparación
        resetOptions: () => {
          console.log('Store: Reseteando opciones');
          set({ options: [] });
        },
        
        resetModules: () => set((state) => ({ modules: [], lastModulesUpdate: 0 })),
        
        getModulesFromApi: async () => {
          // Implementación simplificada - mantener si es necesaria
        },

        // OPTIMIZACIÓN: Función para obtener módulos desde cache
        getModulesFromCache: () => {
          return get().modules;
        },

        // OPTIMIZACIÓN: Función para forzar refresh de módulos
        forceRefreshModules: async () => {
          const state = get();
          set({ isValidatingModules: true, isLoading: true });
          
          try {
            const tokenData = JSON.parse(
              localStorage.getItem(process.env.REACT_APP_TOKEN!) || "{}"
            );
            const token = tokenData?.token ?? null;
            const profileId = tokenData?.user?.profileId ?? state.profileId;

            if (!token || !profileId) {
              console.error("No se encontró un token o profileId");
              return;
            }

            const { data: dataModules } = await getAllModulesByProfileId(profileId);
            console.log(dataModules);
            if (dataModules) {
              // Solo actualizar si los módulos son diferentes
              if (!areModulesEqual(state.modules, dataModules)) {
                const opts = Array.from(
                  new Map(
                    dataModules
                      .flatMap((mod) => mod.options || [])
                      .map((option) => [option.id, option])
                  ).values()
                );
                const moduleMap = buildModuleMap(dataModules);
                
                set({
                  modules: dataModules,
                  options: opts,
                  moduleMap,
                  lastModulesUpdate: Date.now(),
                });
                
                useModulesBackupStore.getState().setModulesBackup(dataModules);
              }
            }
            
            return dataModules;
          } catch (error: any) {
            const { modulesBackup } = useModulesBackupStore.getState();
            
            if (!navigator.onLine || error.response?.status === 401) {
              if (modulesBackup.length > 0 && !areModulesEqual(state.modules, modulesBackup)) {
                const opts = Array.from(
                  new Map(
                    modulesBackup
                      .flatMap((mod) => mod.options || [])
                      .map((option) => [option.id, option])
                  ).values()
                );
                
                set({
                  modules: modulesBackup,
                  options: opts,
                });
                return modulesBackup;
              }
            }
            
            console.error("Error al cargar módulos:", error);
            return;
          } finally {
            set({ isLoading: false, isValidatingModules: false });
          }
        },

        // OPTIMIZACIÓN: Validación inteligente de módulos
        validateModulesFromApi: async () => {
          const state = get();
          
          // Evitar llamadas duplicadas
          if (state.isValidatingModules) {
            return state.modules;
          }

          // Si ya tenemos módulos y fueron actualizados recientemente (menos de 5 minutos), no validar
          const fiveMinutes = 5 * 60 * 1000;
          if (state.modules.length > 0 && (Date.now() - state.lastModulesUpdate) < fiveMinutes) {
            return state.modules;
          }

          set({ isValidatingModules: true, isLoading: true });
          
          try {
            const tokenData = JSON.parse(
              localStorage.getItem(process.env.REACT_APP_TOKEN!) || "{}"
            );
            const token = tokenData?.token ?? null;
            const profileId = tokenData?.user?.profileId ?? state.profileId;

            if (!token || !profileId) {
              console.error("No se encontró un token o profileId");
              set({ isLoading: false, isValidatingModules: false });
              return state.modules;
            }

            const { data: dataModules } = await getAllModulesByProfileId(profileId);

            if (dataModules) {
              // Solo actualizar si los datos son diferentes
              if (!areModulesEqual(state.modules, dataModules)) {
                const opts = Array.from(
                  new Map(
                    dataModules
                      .flatMap((mod) => mod.options || [])
                      .map((option) => [option.id, option])
                  ).values()
                );
                const moduleMap = buildModuleMap(dataModules);
                
                set({
                  modules: dataModules,
                  options: opts,
                  moduleMap,
                  lastModulesUpdate: Date.now(),
                });
                
                useModulesBackupStore.getState().setModulesBackup(dataModules);
                return dataModules;
              } else {
                // Los datos son iguales, solo actualizar timestamp
                set({ lastModulesUpdate: Date.now() });
                return state.modules;
              }
            } else {
              if (state.modules.length > 0) {
                // No limpiar módulos existentes si la respuesta está vacía
                return state.modules;
              }
              set({ modules: [], options: [] });
              return [];
            }
          } catch (error: any) {
            const { modulesBackup } = useModulesBackupStore.getState();
            
            if (!navigator.onLine || error.response?.status === 401) {
              if (modulesBackup.length > 0) {
                if (!areModulesEqual(state.modules, modulesBackup)) {
                  const opts = Array.from(
                    new Map(
                      modulesBackup
                        .flatMap((mod) => mod.options || [])
                        .map((option) => [option.id, option])
                    ).values()
                  );
                  
                  set({
                    modules: modulesBackup,
                    options: opts,
                  });
                }
                return modulesBackup;
              }
            }
            
            console.error("Error en validateModulesFromApi:", error);
            // No limpiar módulos en caso de error si ya tenemos algunos
            if (state.modules.length === 0) {
              set({ modules: [], options: [] });
            }
            return state.modules;
          } finally {
            set({ isLoading: false, isValidatingModules: false });
          }
        },

        getQualityPhoto: async () => {
          try {
            const { data: dataQualityPhoto } = await parameterGetNoAuthByCode(
              QUALITY_PHOTO_IMAGE_CODE
            );

            if (!dataQualityPhoto) return;

            const width_ = dataQualityPhoto.value.split(",")[0];
            const width = width_.split(":")[1];
            const height_ = dataQualityPhoto.value.split(",")[1];
            const height = height_.split(":")[1];

            set({
              photoWidth: { ideal: Number(width) },
              photoHeight: { ideal: Number(height) },
            });
          } catch (error) {
            console.error("Error al obtener calidad de foto:", error);
          }
        },

        // OPTIMIZACIÓN: Actualizar opciones sin comparación estricta para debugging
        setOptions: (options) => {
          console.log('Store: Actualizando opciones a:', options);
          set({ options });
        },

        // OPTIMIZACIÓN: Solo actualizar módulos si son diferentes
        setModules: (modules) => set((state) => {
          if (areModulesEqual(state.modules, modules)) {
            return state;
          }
          const moduleMap = buildModuleMap(modules);
          return { 
            modules, 
            moduleMap,
            lastModulesUpdate: Date.now(),
          };
        }),

        setTypeDashboardCode: (typeDashboardCode) =>
          set((state) => ({ typeDashboardCode })),
        setProfileId: (profileId) => set((state) => ({ profileId })),
        setAvatar: (avatar) => set((state) => ({ avatar })),
        setCloneCatalogue: (cloneCatalogue) =>
          set((state) => ({ cloneCatalogue })),
        setCloneTask: (cloneTask) => set((state) => ({ cloneTask })),
      }),
      { 
        name: process.env.REACT_APP_TOKEN_ROUTER!,
        // OPTIMIZACIÓN: Solo persistir campos necesarios
        partialize: (state) => ({
          clientId: state.clientId,
          client: state.client,
          profileId: state.profileId,
          firstName: state.firstName,
          avatar: state.avatar,
          typeDashboardCode: state.typeDashboardCode,
          fullName: state.fullName,
          photoWidth: state.photoWidth,
          photoHeight: state.photoHeight,
          processedContainersToday: Array.from(state.processedContainersToday),
          currentDateKey: state.currentDateKey,
          // No persistir modules y options para forzar refresh
        }),
         onRehydrateStorage: () => (state) => {
          if (state && Array.isArray(state.processedContainersToday)) {
            state.processedContainersToday = new Set(state.processedContainersToday);
          }
        },
      }
    )
  )
);